// Popup vs redirect for OAuth sign-in (Google / Apple).
//
// Popups are unreliable on mobile browsers — iOS Safari blocks or orphans them —
// so mobile uses signInWithRedirect. FirebaseUI used to make this choice internally;
// when we replaced it with custom buttons that safety net was lost. It now lives
// here, explicitly. Desktop keeps signInWithPopup (no page reload, result stays in
// the same closure). Redirect results are consumed on return by getRedirectResult()
// in ClientLoginScreen and routed into the same success handlers as the popups.

export function isMobileWeb() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPadOS 13+ masquerades as Macintosh; the touch check unmasks it.
  const iPadOS = ua.includes('Macintosh')
    && typeof document !== 'undefined' && 'ontouchend' in document;
  return /iPhone|iPad|iPod|Android/i.test(ua) || iPadOS;
}

// ── Signup-intent carriage across the redirect round-trip ──
// signInWithRedirect unloads the page, so React state recording "the user chose to
// sign up" (isSignup / proceedToSignup) is gone when getRedirectResult delivers the
// auth result. Without it, abortIfAccidentalNewUser treats every legitimate new
// signup as an accidental login-duplicate and DELETES the just-created user — the
// user retries, minting a fresh uid (and an orphaned client) per attempt
// (production incident, 2026-07-27). Persist the intent at redirect time; the
// return path consumes it exactly once. Popup flow never unloads, so it neither
// writes nor needs this.
// localStorage, NOT sessionStorage: much of our signup traffic arrives in social
// in-app webviews (Instagram/Facebook/LinkedIn) whose OAuth round-trip can land in
// a different browsing context — sessionStorage is per-tab and would lose the
// intent there, resurrecting the delete-retry loop. localStorage survives the
// context handoff; consume-once + the 15-min TTL bound any cross-tab staleness.
const PENDING_AUTH_INTENT_KEY = 'strike:pendingAuthIntent';
const PENDING_AUTH_INTENT_LIFETIME_MS = 15 * 60 * 1000;

// INVARIANT: only ClientLoginScreen consumes this flag, and inSignupFlow:true must
// only ever be persisted by a mount where the user explicitly chose to sign up
// (ClientLoginScreen's signup page / create-account interstitial). Other callers of
// signInWithProviderFlow (AdminLoginScreen, ClientStripeAppOAuthScreen) never pass
// isSignup — a leaked {inSignupFlow:true} from elsewhere would silently suppress the
// anti-duplicate abort on the next /client/login mount.
export function persistPendingAuthIntent({ inSignupFlow }) {
  try {
    localStorage.setItem(PENDING_AUTH_INTENT_KEY, JSON.stringify({
      inSignupFlow: !!inSignupFlow,
      ts: new Date().getTime(),
    }));
  } catch (err) { /* storage unavailable — abort guard falls back to login semantics */ }
}

/** Consume-once: reads AND clears, so a stale flag can never suppress the
 *  anti-duplicate abort on a later genuine login. Returns { inSignupFlow } or null. */
export function consumePendingAuthIntent() {
  try {
    const raw = localStorage.getItem(PENDING_AUTH_INTENT_KEY);
    localStorage.removeItem(PENDING_AUTH_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const fresh = parsed && typeof parsed.ts === 'number'
      && (new Date().getTime() - parsed.ts) < PENDING_AUTH_INTENT_LIFETIME_MS;
    return fresh ? { inSignupFlow: !!parsed.inSignupFlow } : null;
  } catch (err) {
    return null;
  }
}

/**
 * Sign in with the given provider using the flow that suits the platform:
 * mobile → signInWithRedirect (resolves to null; the page navigates away and the
 * result arrives via getRedirectResult after the round-trip), desktop →
 * signInWithPopup (resolves with the authResult).
 * `inSignupFlow` records whether the user is knowingly signing up — persisted
 * across the redirect unload so the anti-duplicate abort keeps working.
 */
export async function signInWithProviderFlow(auth, provider, { inSignupFlow = false } = {}) {
  if (isMobileWeb()) {
    persistPendingAuthIntent({ inSignupFlow });
    await auth.signInWithRedirect(provider);
    return null; // navigation takes over; result handled on return
  }
  return auth.signInWithPopup(provider);
}
